import React, { useState, useMemo } from 'react';
import { Box, InputAdornment, Radio, TextField, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import { ModalHeaderTitle, ModalFooter } from './GroupLessonTemplateStyled';

const matchSearch = (name, query) => {
  if (!query) return true;
  return name.toLowerCase().includes(query.toLowerCase());
};

const TemplateSelectModal = ({ open, onClose, templateList = [], selectedTemplateId, onSelect }) => {
  const [search, setSearch] = useState('');
  const [tempSelectedId, setTempSelectedId] = useState(selectedTemplateId || '');

  // 모달 열릴 때 현재 선택값 동기화
  React.useEffect(() => {
    if (open) {
      setTempSelectedId(selectedTemplateId || '');
      setSearch('');
    }
  }, [open, selectedTemplateId]);

  const filteredList = useMemo(
    () => templateList.filter(t => matchSearch(t.templateName, search)),
    [templateList, search],
  );

  const handleConfirm = () => {
    onSelect(tempSelectedId);
    onClose();
  };

  const handleClear = () => {
    setTempSelectedId('');
  };

  const selectedTemplate = templateList.find(t => t.id === tempSelectedId);

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '600px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
      }}>
        <Box sx={{
          backgroundColor: '#111', height: '52px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderRadius: '10px 10px 0 0', padding: '0 20px',
        }}>
          <ModalHeaderTitle>그룹수업 선택</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </Box>

        {/* 검색 */}
        <Box sx={{ p: '12px 16px', backgroundColor: '#fafafa', borderBottom: '1px solid #eee' }}>
          <TextField
            size="small" fullWidth
            placeholder="그룹수업 검색..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#4AB3BC' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '36px', backgroundColor: '#fff', borderRadius: '6px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
              },
            }}
          />
        </Box>

        {/* 템플릿 목록 */}
        <Box sx={{
          minHeight: '300px', maxHeight: '500px', overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
        }}>
          {filteredList.length === 0 ? (
            <Typography sx={{ p: '16px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
              {templateList.length === 0 ? '등록된 그룹수업이 없습니다' : '검색 결과가 없습니다'}
            </Typography>
          ) : (
            filteredList.map(tpl => {
              const isSelected = tempSelectedId === tpl.id;
              return (
                <Box
                  key={tpl.id}
                  onClick={() => setTempSelectedId(prev => prev === tpl.id ? '' : tpl.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: '16px', py: '10px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#f0f8f8' : 'transparent',
                    borderLeft: isSelected ? '3px solid #4AB3BC' : '3px solid transparent',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'all 0.1s ease',
                    '&:hover': { backgroundColor: isSelected ? '#e8f5f5' : '#f8f8f8' },
                  }}>
                  <Radio
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0, mr: 1.5, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: isSelected ? 600 : 400, color: isSelected ? '#333' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tpl.templateName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#999', mt: '2px' }}>
                      {tpl.classDuration}분 · 차감 {tpl.ticketDeductionCount}회 · 정원 {tpl.membersMaxCount}명
                      {tpl.membersWaitMaxCount > 0 && ` · 대기 ${tpl.membersWaitMaxCount}명`}
                      {tpl.selectedTicketCount > 0 && ` · 수강권 ${tpl.selectedTicketCount}개`}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* 푸터 */}
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleConfirm}>
            {tempSelectedId ? '선택 완료' : '선택 없이 닫기'}
          </CommonModalButton>
        </ModalFooter>
      </Box>
    </Modal>
  );
};

export default TemplateSelectModal;
